import React from 'react';
import { observable, action, runInAction, makeObservable, } from 'mobx';
import { observer } from 'mobx-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ImgDragHandle } from '@components/Images/ImgDragHandle';
import { ImgEdit } from '@components/Images/ImgEdit';
import { ImgTrash } from '@components/Images/ImgTrash';

import { type CategoryInfo } from '@root/models/CategoryInfo';
import { PromiseCompletion } from '@classes/PromiseCompletion';
import { apiService } from '@services/ApiService';
import { notificationService } from '@services/NotificationService';
import { ArrayService } from '@services/ArrayService';
import { type IModalDialogContent, type ModalButtonOptions, ModalButtonType, type ModalDialogOptions, modalService, ModalWindow } from '@components/Modal';
import type { RenameCategoryDialogProps } from './RenameCategoryDialog';

const RenameCategoryDialog = React.lazy(() => import('./RenameCategoryDialog'));

interface SortableItemProps {
  value: CategoryInfo;
  onEditClick: (category: CategoryInfo) => void;
  onDeleteClick: (category: CategoryInfo) => void;
}

const SortableItem = observer(({value, onEditClick, onDeleteClick}: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: value.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li ref={setNodeRef} className="category-manage-item" style={style}>
      <span {...attributes} {...listeners} style={{ cursor: 'grab', touchAction: 'none' }}>
        <ImgDragHandle size={16} />
      </span>
      {' '}{value.name || '[No name]'}
      <span className="edit-category" onClick={() => onEditClick(value)}><ImgEdit size={16} /></span>
      <span className="delete-category" onClick={() => onDeleteClick(value)}><ImgTrash size={16} /></span>
    </li>
  );
});

interface SortableCategoriesListProps {
  categories: CategoryInfo[];
  onEditClick: (category: CategoryInfo) => void;
  onDeleteClick: (category: CategoryInfo) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

const SortableCategoriesList: React.FC<SortableCategoriesListProps> = ({ categories, onEditClick, onDeleteClick, onDragEnd }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      }
    })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={categories.map(c => c.id)}
        strategy={verticalListSortingStrategy}
      >
        <ul className="category-manage-container">
          {categories.map((value) => (
            <SortableItem
              key={`item-${value.id}`}
              value={value}
              onEditClick={onEditClick}
              onDeleteClick={onDeleteClick}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
};

export type ManageCategoriesDialogProps = {
  onRefresh: () => void;
};

@observer
export default class ManageCategoriesDialog extends React.Component<ManageCategoriesDialogProps> implements IModalDialogContent<string> {
  @observable private _categories: CategoryInfo[] = [];
  private _loader: PromiseCompletion = new PromiseCompletion();

  constructor(props: ManageCategoriesDialogProps){
    super(props);
    makeObservable(this);
  }

  componentDidMount(){
    void this._loadData();
  }

  public getModalOptions(window: ModalWindow<string>): ModalDialogOptions<string> {
    const buttons: ModalButtonOptions<string>[] = [];
    
    buttons.push({
      type: ModalButtonType.Cancel,
      onClick: () => {
        window.close();
      }
    });

    return {
      title: `Manage categories`,
      width: 600,
      buttons: buttons,
      loader: this._loader
    };
  }

  render(){
    const categories = [...this._categories];
    const sortedNotes = categories.sort((n1, n2) => n1.sortOrder - n2.sortOrder);

    return (
      <>
        <SortableCategoriesList
          categories={sortedNotes}
          onEditClick={this._onEditClick}
          onDeleteClick={this._onDeleteClick}
          onDragEnd={this._handleDragEnd}
        />
      </>
    );
  }
  
  private async _loadData(){
    const data = await apiService.get<CategoryInfo[]>('/api/note/category', null, {
      loader: this._loader
    });
    
    runInAction(() => {
      this._categories = data;
    });
  }
  
  @action.bound
  private async _handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const categories = this._categories;
    const oldIndex = categories.findIndex(c => c.id === active.id);
    const newIndex = categories.findIndex(c => c.id === over.id);

    ArrayService.arrayMoveMutable(categories, oldIndex, newIndex);
    categories.forEach((w, index) => w.sortOrder = index);
    
    const orderModel = {
      ids: categories.map(w => w.id)
    };
    await apiService.put('/api/note/category/reorder', orderModel);
    notificationService.success('Order was saved!');
    
    this.props.onRefresh();
  }
  
  @action.bound
  private async _onEditClick(category: CategoryInfo){
    const { button, result } = await modalService.show<RenameCategoryDialogProps, string>(RenameCategoryDialog, {
      category: category
    });

    if (button !== ModalButtonType.Ok || !result){
      return;
    }
    
    await apiService.put('/api/note/category/' + category.id, { name: result }, {
      loader: this._loader
    });

    runInAction(() => {
      category.name = result;

      this.props.onRefresh();
    });
  }

  @action.bound
  private async _onDeleteClick(category: CategoryInfo){
    if (!await modalService.showConfirmation("Confirmation", `Do you really want to delete category (${category.name ?? '[No name]'})`, { color: 'danger', buttonTitle: 'Remove' })){
      return;
    }
    
    const categoryIndex = this._categories.findIndex(n => n.id === category.id);
    if (categoryIndex !== -1){
      this._categories.splice(categoryIndex, 1);
    }

    await apiService.delete('/api/note/category/' + category.id, {
      loader: this._loader
    });

    this.props.onRefresh();
  }
}